import type { ParticleSystem as ParticleSystemConfig, Shader } from "@/types";
import { extend, invalidate, useFrame, useThree } from "@react-three/fiber";
import { useEffect, useRef, type FC } from "react";
import {
  AddEquation,
  CustomBlending,
  DoubleSide,
  OneFactor,
  ShaderMaterial,
  SrcAlphaFactor,
  Texture,
  Vector3,
  type InstancedMesh,
} from "three";
import { Emitter } from "./Emitter";
import {
  buildColorGradientTexture,
  buildSizeGradientTexture,
} from "./helpers/gradients";
import { mapBlendingMode } from "./helpers/mapBlendingMode";
import { ParticlesMaterial } from "./Material/ParticlesMaterial";

const Particles = extend(ParticlesMaterial);

interface ParticleSystemProps {
  debug?: boolean;
  particleSystem: ParticleSystemConfig;
  texture: Texture;
  isActive: boolean;
  particleShader?: Shader;
  diffuseTexture?: Texture;
  maskTexture?: Texture;
}

export const ParticleSystem: FC<ParticleSystemProps> = ({
  particleSystem,
  texture,
  isActive,
  particleShader,
  diffuseTexture,
  maskTexture,
}) => {
  const materialRef = useRef<ShaderMaterial>(null!);
  const meshRef = useRef<InstancedMesh>(null!);
  const startTimeRef = useRef<number | null>(null);
  const nextParticleIndexRef = useRef(0);
  const emitPosition = useRef(new Vector3());

  const { clock } = useThree();

  const {
    emitter,
    colorDead,
    capacity,
    renderingGroupId,
    updateSpeed,
    targetStopDuration,
    customShader,
    maxLifeTime,
    blendMode,
    isLocal,
    gravity,
  } = particleSystem;

  // BabylonJS defaults update speed to 0.01. It then uses this multiplier all
  // over the place to control how fast particles animate. We centralize it here
  // by multiplying the system's current time to update faster or slower. To get
  // this value to work as a multiplier, we normalize it with the default 0.01.
  //
  // The 0.65 multiplier is experimental to get the feel closer to Babylon's speed.
  const shaderUpdateSpeed = (updateSpeed / 0.01) * 0.65;

  // Additive blend modes (0, 2) don't need depth testing - they render "on top"
  // Normal/standard blend modes (1, 3, 4) need depth testing to render correctly
  const depthTestEnabled =
    blendMode === 1 || blendMode === 3 || blendMode === 4;

  // Reset state when isActive transitions to true
  useEffect(() => {
    if (isActive) {
      nextParticleIndexRef.current = 0;
      startTimeRef.current = clock.getElapsedTime();
    } else if (startTimeRef.current) {
      // Only run this once the system has been assigned a start time.
      // Otherwise, the stop time will always be set to 0 because we initialize
      // the system with `isActive = false`
      const age = clock.getElapsedTime() - startTimeRef.current;
      materialRef.current.uniforms.uStopEmitTime.value = age;
    }
  }, [isActive, clock]);

  useFrame(({ clock }) => {
    if (!materialRef.current || !startTimeRef.current) return;

    // We can return early and stop rendering the system once the last particle
    // has died, if we have defined a stop emit time. We also want the entire
    // system to speed up or slow down depending on the updateSpeed, so we
    // multiply the age by it here.
    const age =
      (clock.getElapsedTime() - startTimeRef.current) * shaderUpdateSpeed;
    const stopEmitTime = materialRef.current.uniforms.uStopEmitTime.value;
    if (stopEmitTime > 0 && age >= maxLifeTime + stopEmitTime) {
      startTimeRef.current = null;
      return;
    }

    materialRef.current.uniforms.uTime.value = age;

    // World-space particles: capture world position at emit time for each particle
    if (!isLocal && meshRef.current) {
      const geometry = meshRef.current.geometry;
      const timingAttr = geometry.getAttribute("aTimingAndSize");
      const localPositionAttr = geometry.getAttribute("aPosAndRotation");
      const worldPositionAttr = geometry.getAttribute(
        "instanceEmitWorldPosition",
      );
      if (!timingAttr || !localPositionAttr || !worldPositionAttr) return;

      let needsUpdate = false;
      // Emit times are sorted, so we can start from the next particle to emit
      // and stop as soon as we hit one that hasn't reached its emit time yet
      while (nextParticleIndexRef.current < capacity) {
        const i = nextParticleIndexRef.current;
        const emitTime = timingAttr.array[i * 4 + 1]; // emitTime is at index 1 in the vec4

        // If this particle's emit time hasn't been reached, stop
        // All subsequent particles will have later emit times
        if (age < emitTime) break;

        emitPosition.current.set(
          localPositionAttr.array[i * 4],
          localPositionAttr.array[i * 4 + 1],
          localPositionAttr.array[i * 4 + 2],
        );

        meshRef.current.localToWorld(emitPosition.current);

        // Store the world position at emit time
        const i3 = i * 3;
        worldPositionAttr.array[i3] = emitPosition.current.x;
        worldPositionAttr.array[i3 + 1] = emitPosition.current.y;
        worldPositionAttr.array[i3 + 2] = emitPosition.current.z;
        nextParticleIndexRef.current++;
        needsUpdate = true;
      }

      // Update the buffer attribute if any particles were emitted
      if (needsUpdate) {
        worldPositionAttr.needsUpdate = true;
      }

      invalidate();
    }
  });

  // Blending mode
  useEffect(() => {
    if (!materialRef.current) return;

    const blendMode = mapBlendingMode(particleSystem.blendMode);
    materialRef.current.blending = blendMode;
    if (blendMode === CustomBlending && particleSystem.blendMode === 2) {
      materialRef.current.blendSrc = SrcAlphaFactor;
      materialRef.current.blendDst = OneFactor;
      materialRef.current.blendEquation = AddEquation;
    }
    materialRef.current.needsUpdate = true;
  }, [particleSystem.blendMode]);

  // Color over time
  useEffect(() => {
    const { colorGradients } = particleSystem;

    if (!(materialRef.current && colorGradients?.length > 0)) return;

    const [colorTexture, stopTexture] = buildColorGradientTexture(
      colorGradients,
      capacity,
    );
    materialRef.current.uniforms["uColors"] = { value: colorTexture };
    materialRef.current.uniforms["uColorsCount"] = {
      value: colorGradients.length * 4 * capacity,
    };
    materialRef.current.uniforms["uColorStops"] = { value: stopTexture };
    materialRef.current.uniforms["uColorStopCount"] = {
      value: colorGradients.length,
    };

    materialRef.current.needsUpdate = true;
  }, [particleSystem, capacity]);

  // Size over time
  useEffect(() => {
    const { sizeGradients } = particleSystem;

    if (!(materialRef.current && sizeGradients?.length > 0)) return;

    const [sizeTexture, sizeStopTexture] = buildSizeGradientTexture(
      sizeGradients,
      capacity,
    );
    materialRef.current.uniforms["uSizes"] = { value: sizeTexture };
    materialRef.current.uniforms["uSizesCount"] = {
      value: sizeGradients.length * capacity,
    };
    materialRef.current.uniforms["uSizeStops"] = { value: sizeStopTexture };
    materialRef.current.uniforms["uSizeStopCount"] = {
      value: sizeGradients.length,
    };
    materialRef.current.needsUpdate = true;
  }, [particleSystem, capacity]);

  // Use sprite sheets
  useEffect(() => {
    const { isAnimationSheetEnabled } = particleSystem;
    if (!(materialRef.current && isAnimationSheetEnabled)) return;

    const {
      startSpriteCellID,
      endSpriteCellID,
      spriteCellChangeSpeed,
      spriteCellWidth,
      spriteCellHeight,
      spriteRandomStartCell,
      spriteCellLoop,
    } = particleSystem;

    materialRef.current.uniforms["uIsAnimationSheetEnabled"] = { value: true };
    materialRef.current.uniforms["uStartSpriteCellId"] = {
      value: startSpriteCellID,
    };
    materialRef.current.uniforms["uEndSpriteCellId"] = {
      value: endSpriteCellID,
    };
    materialRef.current.uniforms["uSpriteCellChangeSpeed"] = {
      value: spriteCellChangeSpeed,
    };
    materialRef.current.uniforms["uSpriteCellWidth"] = {
      value: spriteCellWidth,
    };
    materialRef.current.uniforms["uSpriteCellHeight"] = {
      value: spriteCellHeight,
    };
    materialRef.current.uniforms["uSpriteRandomStartCell"] = {
      value: spriteRandomStartCell,
    };
    materialRef.current.uniforms["uSpriteCellLoop"] = { value: spriteCellLoop };
  }, [particleSystem]);

  return (
    <>
      <instancedMesh
        ref={meshRef}
        args={[undefined, undefined, capacity]}
        position={emitter} // confusing, but emitter is the key for position in Babylon's particle system config
        renderOrder={renderingGroupId}
      >
        <planeGeometry args={[1, 1]} />
        <Particles
          ref={materialRef}
          uTexture={texture}
          uTextureSize={[texture.image.width, texture.image.height]}
          uColorDead={colorDead}
          transparent={true}
          depthWrite={false}
          alphaTest={0.01}
          side={DoubleSide}
          depthTest={depthTestEnabled}
          diffuseSampler={diffuseTexture}
          maskTexture={maskTexture}
          uStopEmitTime={targetStopDuration}
          uIsLocal={isLocal}
          uAcceleration={gravity}
          onBeforeCompile={(shader) => {
            if (customShader && particleShader) {
              shader.fragmentShader = particleShader.fragmentShader;
            }
          }}
          customProgramCacheKey={() =>
            customShader && particleShader ? particleSystem.name : "default"
          }
        />
      </instancedMesh>
      <Emitter config={particleSystem} meshRef={meshRef} />
    </>
  );
};
